import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useOrganizacion } from '../context/OrganizacionContext';
import { evaluacionService } from '../services/api';

const EvaluacionDinamica = ({ 
  tipoEvaluacion = 'lider_empleado', 
  evaluadorId, 
  evaluadoId, 
  evaluadorInfo = {}, 
  evaluadoInfo = {} 
}) => {
  const [plantilla, setPlantilla] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [comentarios, setComentarios] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { config, logoUrl } = useOrganizacion();

  useEffect(() => {
    cargarPlantilla();
  }, [tipoEvaluacion]);

  const cargarPlantilla = async () => {
    try {
      setLoading(true);
      const data = await evaluacionService.dinamicas.getPlantillaDinamica(tipoEvaluacion);
      setPlantilla(data);
      
      // Inicializar respuestas vacías como null (no string vacío)
      const initialRespuestas = {};
      data.preguntas.forEach(pregunta => {
        initialRespuestas[pregunta.id] = null;
      });
      setRespuestas(initialRespuestas);
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: parseInt(valor)
    }));
  };

  const calcularPromedio = () => {
    const valores = Object.values(respuestas).filter(r => r);
    if (valores.length === 0) return 0;
    
    const suma = valores.reduce((acc, curr) => acc + parseFloat(curr), 0);
    return (suma / valores.length).toFixed(1);
  };

  const calcularPromedioPorCategoria = () => {
    if (!plantilla) return {};
    
    const categorias = {};
    plantilla.preguntas.forEach(pregunta => {
      if (!categorias[pregunta.categoria]) {
        categorias[pregunta.categoria] = {
          valores: [],
          peso_total: 0
        };
      }
      
      const respuesta = respuestas[pregunta.id];
      if (respuesta) {
        categorias[pregunta.categoria].valores.push(respuesta * pregunta.peso);
        categorias[pregunta.categoria].peso_total += pregunta.peso;
      }
    });

    const promedios = {};
    Object.keys(categorias).forEach(categoria => {
      const { valores, peso_total } = categorias[categoria];
      if (valores.length > 0) {
        promedios[categoria] = (valores.reduce((a, b) => a + b, 0) / peso_total).toFixed(1);
      }
    });

    return promedios;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que todas las preguntas estén respondidas
    const preguntasFaltantes = plantilla.preguntas.filter(p => !respuestas[p.id] || respuestas[p.id] < 1 || respuestas[p.id] > 5);
    
    if (preguntasFaltantes.length > 0) {
      toast.error('Por favor responda todas las preguntas antes de enviar');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Preparar las respuestas en el formato esperado por el backend
      // SOLO enviar preguntas que tienen respuesta válida (puntuación 1-5)
      // El comentario general solo se guarda en la primera pregunta
      const respuestasFormateadas = plantilla.preguntas
        .filter(pregunta => {
          const puntuacion = respuestas[pregunta.id];
          const puntuacionNum = parseInt(puntuacion, 10);
          const esValida = puntuacion !== null && puntuacion !== undefined && !Number.isNaN(puntuacionNum) && puntuacionNum >= 1 && puntuacionNum <= 5;
          return esValida;
        })
        .map((pregunta, index) => ({
          pregunta_id: String(pregunta.id),
          pregunta_texto: pregunta.texto,
          evaluado_id: String(evaluadoId),
          evaluador_id: String(evaluadorId),
          tipo_evaluacion: tipoEvaluacion,
          puntuacion: parseInt(respuestas[pregunta.id], 10),
          // Solo guardar el comentario en la primera pregunta
          comentarios: index === 0 && comentarios.trim() ? comentarios.trim() : null
        }));

      await evaluacionService.dinamicas.submitRespuestasDinamicas(respuestasFormateadas);
      
      toast.success('¡Evaluación enviada exitosamente!');
      navigate('/login-unificado');
      
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getEscalaLabels = () => [
    { valor: 5, label: 'Excelente', descripcion: 'Supera consistentemente las expectativas', color: 'text-green-600' },
    { valor: 4, label: 'Muy Bueno', descripcion: 'Cumple y ocasionalmente supera las expectativas', color: 'text-blue-600' },
    { valor: 3, label: 'Satisfactorio', descripcion: 'Cumple con las expectativas básicas', color: 'text-yellow-600' },
    { valor: 2, label: 'Necesita Mejora', descripcion: 'Por debajo de las expectativas mínimas', color: 'text-orange-600' },
    { valor: 1, label: 'Insatisfactorio', descripcion: 'Muy por debajo de las expectativas', color: 'text-red-600' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando evaluación...</p>
        </div>
      </div>
    );
  }

  if (error && !plantilla) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="text-red-800">
            <h3 className="text-lg font-medium">Error al cargar la evaluación</h3>
            <p className="mt-2">{error}</p>
            <button 
              onClick={() => navigate('/login-unificado')}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  const categoriasPromedios = calcularPromedioPorCategoria();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="text-center">
              {/* Logo de la organización */}
              {logoUrl && config.mostrar_logo && (
                <div className="flex justify-center mb-4">
                  <img
                    src={logoUrl}
                    alt={config.nombre_organizacion || 'Logo'}
                    className="h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ color: config.color_primario }}
              >
                {plantilla?.titulo}
              </h1>
              <p className="text-gray-600 mb-4">
                {plantilla?.descripcion}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${config.color_primario}15` }}
                >
                  <p><strong>Evaluador:</strong> {evaluadorInfo.nombre || 'N/A'}</p>
                  <p><strong>Cargo:</strong> {evaluadorInfo.cargo || 'N/A'}</p>
                </div>
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${config.color_secundario}15` }}
                >
                  <p><strong>Evaluado:</strong> {evaluadoInfo.nombre || 'N/A'}</p>
                  <p><strong>Cargo:</strong> {evaluadoInfo.cargo || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Progreso */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm font-medium text-gray-700">
                  Progreso: {Object.values(respuestas).filter(r => r).length}/{plantilla?.preguntas?.length || 0}
                </span>
                <span className="text-sm font-medium text-blue-600">
                  Promedio general: {calcularPromedio()}/5.0
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${plantilla?.preguntas ? (Object.values(respuestas).filter(r => r).length / plantilla.preguntas.length) * 100 : 0}%` 
                  }}
                ></div>
              </div>
              
              {/* Promedios por categoría */}
              {Object.keys(categoriasPromedios).length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  {Object.entries(categoriasPromedios).map(([categoria, promedio]) => (
                    <div key={categoria} className="bg-gray-50 p-2 rounded">
                      <span className="font-medium">{categoria}:</span> {promedio}/5.0
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preguntas por categoría */}
            {plantilla && plantilla.preguntas && (
              <div className="space-y-6">
                {/* Agrupar preguntas por categoría */}
                {Object.entries(
                  plantilla.preguntas.reduce((acc, pregunta) => {
                    if (!acc[pregunta.categoria]) {
                      acc[pregunta.categoria] = [];
                    }
                    acc[pregunta.categoria].push(pregunta);
                    return acc;
                  }, {})
                ).map(([categoria, preguntasCategoria]) => (
                  <div key={categoria} className="bg-white rounded-lg shadow-sm">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg">
                      <h3 className="text-lg font-semibold">{categoria}</h3>
                      <p className="text-sm opacity-90">
                        {preguntasCategoria.length} pregunta{preguntasCategoria.length !== 1 ? 's' : ''} 
                        • Peso total: {(preguntasCategoria.reduce((sum, p) => sum + p.peso, 0) * 100).toFixed(0)}%
                      </p>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {preguntasCategoria.map((pregunta, index) => (
                        <div key={pregunta.id} className="border-b border-gray-100 pb-6 last:border-b-0">
                          <div className="mb-4">
                            <h4 className="text-base font-medium text-gray-900 mb-2">
                              {index + 1}. {pregunta.texto}
                            </h4>
                            <div className="text-xs text-gray-500 flex items-center space-x-4">
                              <span>Peso: {(pregunta.peso * 100).toFixed(0)}%</span>
                              {respuestas[pregunta.id] && (
                                <span className="text-blue-600 font-medium">
                                  Puntuación: {respuestas[pregunta.id]}/5
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            {getEscalaLabels().map((opcion) => {
                              const isSelected = respuestas[pregunta.id] === opcion.valor;
                              return (
                                <label
                                  key={opcion.valor}
                                  className={`
                                    relative flex flex-col cursor-pointer rounded-lg border-2 p-3 transition-all
                                    ${isSelected 
                                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                                      : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
                                    }
                                  `}
                                >
                                  <input
                                    type="radio"
                                    name={`pregunta_${pregunta.id}`}
                                    value={opcion.valor}
                                    checked={isSelected}
                                    onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                                    className="sr-only"
                                    required
                                  />
                                  <div className="text-center">
                                    <div className={`text-xl font-bold mb-1 ${isSelected ? 'text-blue-600' : opcion.color}`}>
                                      {opcion.valor}
                                    </div>
                                    <div className={`text-sm font-medium mb-1 ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                                      {opcion.label}
                                    </div>
                                    <div className="text-xs text-gray-500 leading-tight">
                                      {opcion.descripcion}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Comentarios adicionales */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Comentarios adicionales (opcional)
              </label>
              <textarea
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Comparta cualquier observación adicional sobre esta evaluación..."
              />
            </div>

            {/* Botón de envío */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => navigate('/login-unificado')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  disabled={submitting || Object.values(respuestas).filter(r => r).length !== plantilla?.preguntas?.length}
                  className={`px-8 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    submitting || Object.values(respuestas).filter(r => r).length !== plantilla?.preguntas?.length
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {submitting ? 'Enviando...' : 'Enviar Evaluación'}
                </button>
              </div>
              
              {Object.values(respuestas).filter(r => r).length !== plantilla?.preguntas?.length && (
                <p className="mt-2 text-sm text-amber-600">
                  Complete todas las preguntas para enviar la evaluación
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EvaluacionDinamica;